import { assign, createMachine } from 'xstate';
import { choose } from 'xstate/lib/actions';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { contextCreator } from '~/machines/utils/context';
import { LDClient, LDFlags } from '~/providers';
import { SelfAttestationData } from '~/screens/vnm-terms';
import { fetchDocuSignEnvelopes, requestDocuSignEnvelope } from '~/services';
import {
  DOCU_SIGN_LMI_STATES,
  DOCU_SIGN_POLLING_INTERVAL,
  DOCU_SIGN_POLLING_TIMEOUT,
} from '~/utils/constants';
import { getUserIpFromStore, sendToSegment, trackError } from '~/utils/tracking';
import { DocuSignContext, DocuSignServices } from './types';

export const docuSignMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as DocuSignContext,
        services: {} as DocuSignServices,
      },
      context: {
        contracts: [],
        envelope: undefined,
        envelopes: undefined,
        signedStatus: '',
        straightToTimeout: false,
        useCase: 'ipa', // parent should pass in
        userId: -1, // parent should pass in
        utilityAccountId: -1, // parent should pass in
        funnelPacket: {} as DocuSignContext['funnelPacket'], // parent should pass in
      },
      initial: 'start',
      states: {
        start: {
          id: 'start',
          always: [
            { cond: 'isOff', target: 'timeout' },
            {
              cond: 'hasReturnedFromDocuSign',
              target: 'sendInstantAlert',
            },
            { target: 'polling' },
          ],
        },
        polling: {
          id: 'polling',
          after: {
            pollingTimeout: [
              { cond: 'isNJorNYUtility', target: 'completed' },
              { cond: 'isMDUtility', target: 'completed' },
              { cond: 'isDEUtility', target: 'completed' },
              { cond: 'isMinnesotaLMI', target: 'completed' },
              { target: 'timeout' },
            ],
          },
          initial: 'fetching',
          states: {
            fetching: {
              invoke: {
                id: 'fetchExistingEnvelopes',
                src: 'fetchExistingEnvelopes',
                onDone: { target: 'deciding', actions: 'setEnvelopes' },
                onError: [
                  {
                    cond: 'isNJorNYUtility',
                    target: '#completed',
                    actions: 'reportPollingError',
                  },
                  {
                    cond: 'isMDUtility',
                    target: '#completed',
                    actions: 'reportPollingError',
                  },
                  {
                    cond: 'isDEUtility',
                    target: '#completed',
                    actions: 'reportPollingError',
                  },
                  { target: '#timeout', actions: 'reportPollingError' },
                ],
              },
            },
            deciding: {
              always: [
                { cond: 'hasPreviouslySigned', target: '#completed' },
                { cond: 'hasContractReady', target: '#ready' },
                { cond: 'hasNotGenerated', target: 'generating' },
                { target: 'waiting' },
              ],
            },
            generating: {
              invoke: {
                id: 'generateEnvelope',
                src: 'generateEnvelope',
                onDone: { target: 'waiting', actions: 'setGenerationRequested' },
                onError: [
                  {
                    cond: 'isNJorNYUtility',
                    target: '#completed',
                    actions: 'reportGenerationError',
                  },
                  {
                    cond: 'isMDUtility',
                    target: '#completed',
                    actions: 'reportGenerationError',
                  },
                  {
                    cond: 'isDEUtility',
                    target: '#completed',
                    actions: 'reportGenerationError',
                  },
                  { target: '#timeout', actions: 'reportGenerationError' },
                ],
              },
            },
            waiting: {
              after: {
                pollingInterval: 'fetching',
              },
            },
          },
        },
        ready: {
          id: 'ready',
          entry: 'redirectToDocuSign',
        },
        timeout: {
          id: 'timeout',
          on: {
            TIMEOUT_BUTTON_CLICKED: {
              target: 'completed',
            },
          },
        },
        sendInstantAlert: {
          always: [
            {
              target: 'completed',
              actions: choose([{ cond: 'isLmiState', actions: 'trackDocusignStatus' }]),
            },
          ],
        },
        completed: {
          id: 'completed',
          type: 'final',
        },
      },
    },
    {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
      actions: {
        redirectToDocuSign: context => {
          const url = context.envelope?.url;
          if (url) window.location.assign(url);
        },
        reportPollingError: context => {
          const payload = { extras: { userId: context.userId } };
          trackError(new Error('DocuSignPollingError'), payload);
        },
        reportGenerationError: context => {
          const payload = { extras: { userId: context.userId } };
          trackError(new Error('DocuSignGenerationError'), payload);
        },
        setEnvelopes: assign((_, event) => {
          const envelopeToSign = event.data.find(c => c.status === 'sent');

          return {
            envelope: envelopeToSign,
            envelopes: event.data,
          };
        }),
        setGenerationRequested: assign(_context => ({
          requestedEnvelopeGeneration: true,
        })),
        /* eslint-disable camelcase */
        trackDocusignStatus: context => {
          const { user, inviteCode } = context.funnelPacket;
          const { firstName, lastName, email, phone, promoCode } = user;
          sendToSegment({
            type: 'track_docusign_status',
            funnelPacket: context.funnelPacket,
            additional: {
              docusignStatus: context.signedStatus!,
              event_message: 'LMI Attestation Signed Status',
              first_name: firstName,
              last_name: lastName,
              email,
              phone,
              userIp: getUserIpFromStore(),
              inviteCode: inviteCode!,
              promo_code: promoCode || context.promoCode,
            },
          });
        },
        /* eslint-enable camelcase */
      },
      guards: {
        isOff: context => context.straightToTimeout,
        // Note: 'signed' means each user can only sign one envelope.
        // a 'completed' envelope has been signed by all relevant signers,
        // copies have been sent as directed by the envelope's sender, etc.
        hasPreviouslySigned: context =>
          !!context.envelopes?.find(c => ['signed', 'completed'].includes(c.status!)),
        hasContractReady: context => !!context.envelope,
        hasNotGenerated: context => !context.requestedEnvelopeGeneration,
        hasReturnedFromDocuSign: context => {
          const allowedStatuses = new Set(['signing_complete', 'decline', 'cancel']);
          const isAllowedStatus = allowedStatuses.has(context.signedStatus || '');
          return isAllowedStatus;
        },
        isMinnesotaLMI: context => {
          return context?.funnelPacket?.utility?.state?.abbreviation === 'MN';
        },
        isLmiState: context => {
          return DOCU_SIGN_LMI_STATES.includes(context?.funnelPacket?.utility?.state?.abbreviation);
        },
        isNJorNYUtility: context => {
          const redirectToLegal =
            fetchFlagValue(
              LDFlags.showLmiSelfAttestationPostPayment,
              'showLmiSelfAttestationPostPayment',
            ) || localStorage.getItem('showLmiSelfAttestationPostPayment') === 'true';
          return (redirectToLegal && isNjUtility(context)) || isNyUtility(context);
        },
        isMDUtility: context => {
          const useGeoCoderForMd = fetchFlagValue(LDFlags.ffUseGeoCoderForMd, 'useGeoCoderForMd');
          return useGeoCoderForMd && isMDUtility(context);
        },
        isDEUtility: context => context.funnelPacket?.utility?.state?.abbreviation === 'DE',
      },
      delays: {
        pollingInterval: DOCU_SIGN_POLLING_INTERVAL,
        pollingTimeout: DOCU_SIGN_POLLING_TIMEOUT,
      },
      services: {
        fetchExistingEnvelopes: async context => {
          const { docuSignEnvelopes } = await fetchDocuSignEnvelopes({ userId: context.userId! });
          return docuSignEnvelopes;
        },
        generateEnvelope: async context => {
          const params = {
            contractVersionIds: context.contracts.map(cv => cv.id),
            userId: context.userId,
            utilityAccountId: context.utilityAccountId,
          } as {
            contractVersionIds: number[];
            userId: number;
            utilityAccountId: number;
            tabsData: SelfAttestationData;
          };

          if (context.selfAttestationData) {
            params.tabsData = context.selfAttestationData;
          }
          await requestDocuSignEnvelope(params);
          return undefined;
        },
      },
    },
  );

export const docuSignContext = contextCreator<
  DocuSignContext,
  'contracts' | 'userId' | 'utilityAccountId'
>(docuSignMachine);

const fetchFlagValue = (flagName: string, localName: string): boolean => {
  const flagValue = LDClient.variation(flagName) || localStorage.getItem(localName) === 'true';
  if (flagValue) {
    localStorage.setItem(localName, flagValue);
  }
  return flagValue;
};

const isNjUtility = context => {
  return (
    context.funnelPacket?.user?.originationSource?.funnel === GO_SOLAR_FUNNEL_ID &&
    context.funnelPacket.utility?.state?.abbreviation === 'NJ'
  );
};

const isNyUtility = context => {
  return context.funnelPacket.utility?.state?.abbreviation === 'NY';
};

const isMDUtility = context => {
  return context.funnelPacket.utility?.state?.abbreviation === 'MD';
};
